import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { StarIcon, UserIcon, DollarSignIcon, MailIcon, Share2Icon } from 'lucide-react';
import ProfileLayout, { ProfileData } from '../../components/Profile/ProfileLayout';
import { ServicesTab, ReviewsTab, AboutTab, PortfolioTab, ServiceTier, Review, PortfolioItem } from '../../components/Profile/ProfileTabs';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
// --- Mock Data ---
const serviceProviderProfile: ProfileData = {
  id: 'sp1',
  name: "Emma's Editing Services",
  role: 'Developmental Editor',
  location: 'New York, NY (Remote)',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&h=400&q=80',
  coverImage: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&h=400&q=80',
  status: 'Available',
  queueStatus: 'open',
  slotsAvailable: 2,
  maxSlots: 4,
  estimatedWait: '1 month',
  rating: 4.9,
  bio: "Specializing in developmental editing for romance and fantasy novels with 10+ years of experience. I help authors refine their stories, strengthen character arcs, and ensure plot coherence while maintaining their unique voice. I've edited over 200 books, many of which have gone on to become bestsellers.",
  specialties: ['Romance', 'Fantasy', 'YA', 'Character Development', 'Plot Structure'],
  stats: [{
    label: 'Rating',
    value: 4.9
  }, {
    label: 'Projects',
    value: 200
  }, {
    label: 'Reviews',
    value: 127
  }, {
    label: 'Years Exp',
    value: '10+'
  }],
  social: {
    website: 'https://emmasediting.com',
    twitter: '@EmmaEdits',
    linkedin: 'emma-editor'
  }
};
const services: ServiceTier[] = [{
  id: 's1',
  name: 'Developmental Editing',
  price: '$0.08/word',
  description: 'In-depth analysis of plot, characters, pacing, and structure with detailed feedback.',
  features: ['Comprehensive Editorial Letter', 'Inline Comments & Suggestions', 'Plot & Pacing Analysis', 'Character Arc Review', '1-hour Follow-up Call'],
  turnaround: '4-6 weeks',
  revisions: '1 round',
  category: 'Editing',
  isPopular: true
}, {
  id: 's2',
  name: 'Manuscript Evaluation',
  price: '$0.04/word',
  description: 'Overall assessment of your manuscript with a detailed editorial letter.',
  features: ['Editorial Letter (5-10 pages)', 'Marketability Assessment', 'Major Plot Hole Identification', 'Pacing Overview'],
  turnaround: '2-3 weeks',
  revisions: 'None',
  category: 'Editing'
}, {
  id: 's3',
  name: 'Coaching Sessions',
  price: '$75/hour',
  description: 'One-on-one coaching to discuss your writing and develop specific skills.',
  features: ['Video Call via Zoom', 'Specific Problem Solving', 'Brainstorming', 'Career Advice'],
  turnaround: 'Flexible',
  revisions: 'N/A',
  category: 'Coaching'
}];
const reviews: Review[] = [{
  id: 'r1',
  clientName: 'Sarah Johnson',
  clientRole: 'Fantasy Author',
  clientAvatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100',
  rating: 5,
  comment: 'Emma transformed my manuscript from a rough draft into a polished novel. Her insights on character development were invaluable.',
  date: 'Sep 2023',
  projectType: 'Dev Edit',
  completionTime: '5 weeks'
}, {
  id: 'r2',
  clientName: 'Robert Chen',
  clientRole: 'Romance Author',
  clientAvatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100',
  rating: 5,
  comment: 'Working with Emma was the best decision I made for my book. She understood exactly what I was trying to achieve and helped me get there.',
  date: 'Aug 2023',
  projectType: 'Manuscript Eval',
  completionTime: '2 weeks'
}];
const portfolioItems: PortfolioItem[] = [{
  id: 'p1',
  title: 'The Hidden Path',
  category: 'Fantasy',
  imageUrl: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  description: 'Developmental edit for a YA Fantasy bestseller.',
  aspectRatio: 'portrait',
  serviceCategory: 'Editing'
}, {
  id: 'p2',
  title: 'Midnight Whispers',
  category: 'Romance',
  imageUrl: 'https://images.unsplash.com/photo-1610882648335-ced8fc8fa6b6?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  description: 'Full developmental and line edit for a contemporary romance.',
  aspectRatio: 'portrait',
  serviceCategory: 'Editing'
}, {
  id: 'p3',
  title: 'Beyond the Stars',
  category: 'Sci-Fi',
  imageUrl: 'https://images.unsplash.com/photo-1518744386442-2d48ac5b5aa1?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  description: 'Manuscript evaluation for a space opera debut.',
  aspectRatio: 'portrait',
  serviceCategory: 'Editing'
}];
const skills = ['Developmental Editing', 'Line Editing', 'Copy Editing', 'Story Structure', 'Character Arc', 'World Building'];
const tools = ['Microsoft Word', 'Chicago Manual of Style', 'Google Docs', 'Scrivener'];
const willDo = ['Romance', 'Fantasy', 'Young Adult', 'Sci-Fi', 'Memoir'];
const wontDo = ['Horror', 'Erotica', 'Academic Texts', 'Poetry'];
const ServiceProviderProfile = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [activeTab, setActiveTab] = useState('services');
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const tabs = [{
    id: 'services',
    label: 'Services & Rates',
    icon: DollarSignIcon,
    count: services.length
  }, {
    id: 'portfolio',
    label: 'Portfolio',
    icon: StarIcon,
    count: portfolioItems.length
  }, {
    id: 'about',
    label: 'About',
    icon: UserIcon
  }, {
    id: 'reviews',
    label: 'Reviews',
    icon: StarIcon,
    count: reviews.length
  }];
  const actionButtons = <>
      <button className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-colors shadow-lg flex items-center gap-2 text-white ${rt.primaryButton}`} data-id="element-13625" data-mp="1x5j8ys0ncset">
        <MailIcon className="w-4 h-4" data-id="element-13626" data-mp="1na7zop80cdeg" />
        Request Quote
      </button>
      <button className={`px-6 py-2.5 rounded-lg text-sm font-bold border transition-colors flex items-center gap-2 ${dk.card}`} data-id="element-13627" data-mp="1ihy2qp8h6d2o">
        <Share2Icon className="w-4 h-4" data-id="element-13628" data-mp="1eh4gs1elf8cf" />
        Share
      </button>
    </>;
  return <ProfileLayout profile={serviceProviderProfile} tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} actionButtons={actionButtons} data-id="element-13629" data-mp="19jcp98hv1o8r">
      {activeTab === 'services' && <ServicesTab services={services} data-id="element-13630" data-mp="zkdpkff2p2xw" />}

      {activeTab === 'portfolio' && <PortfolioTab items={portfolioItems} services={services} data-id="element-13631" data-mp="pevo4nmybgy4" />}

      {activeTab === 'about' && <AboutTab profile={serviceProviderProfile} skills={skills} tools={tools} willDo={willDo} wontDo={wontDo} data-id="element-13632" data-mp="ud70b50py3ji" />}

      {activeTab === 'reviews' && <ReviewsTab reviews={reviews} data-id="element-13633" data-mp="14c668fu3nl2t" />}
    </ProfileLayout>;
};
export default ServiceProviderProfile;